import Button from '@rio-cloud/rio-uikit/Button';
import SmoothScrollbars from '@rio-cloud/rio-uikit/components/smoothScrollbars/SmoothScrollbars';
import Fade from '@rio-cloud/rio-uikit/Fade';
import isUndefined from 'lodash/fp/isUndefined';
import { lazy, useCallback, useState } from 'react';
import { Flags } from 'react-feature-flags';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import { DEFAULT_ROUTE, SUMMARY_ROUTE } from '../../common/constants';
import { shouldBlockSyncForExpiredDeparture } from '../../common/tourUtils';
import { PageNavigationBar } from '../../components/PageNavigationBar/PageNavigationBar';
import { PastDepartureBlockDialog } from '../../components/PastDepartureBlockDialog/PastDepartureBlockDialog';
import { useAppDispatch, useAppSelector } from '../../configuration/setup/hooks';
import { trace } from '../../configuration/setup/trace';
import { SectionsContainer } from '../../containers/SectionsContainer/SectionsContainer';
import { TourSummary } from '../../containers/TourSummary/TourSummary';
import { VehicleSelection } from '../../containers/VehicleSelection/VehicleSelection';
import { propagateRoutes } from '../../features/livemonitorCommunication/MapRouteRendering';
import { selectPageDisplayed } from '../../store/app/appSelectors';
import { updatePage } from '../../store/app/appSlice';
import { Reason, useSendTourToOptimizerMutation, useSendTourToVehicleMutation } from '../../store/facade/facadeApiV3';
import {
    selectCurrentTour,
    selectItineraries,
    selectSelectedItinerary,
    selectTourId,
    selectTourItineraryId,
    selectTourVehicleId,
} from '../../store/tour/tourSelectors';
import { updateTourId } from '../../store/tour/tourSlice';
import { NotificationType, showMapNotification } from '../../store/widget/actions/showMapNotification';

const DownloadPdfButton = lazy(() =>
    import('../../components/DownloadPDF/DownloadPDF').then(module => ({ default: module.DownloadPDF }))
);

export const RouteSummaryPage = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [showPastDepartureDialog, setShowPastDepartureDialog] = useState(false);

    const showContent = useAppSelector(selectPageDisplayed) === SUMMARY_ROUTE;
    const itineraries = useAppSelector(selectItineraries);
    const selectedItinerary = useAppSelector(selectSelectedItinerary);
    const selectedRouteData = itineraries?.at(selectedItinerary ?? 0);
    const assetId = useAppSelector(selectTourVehicleId);
    const tourId = useAppSelector(selectTourId);
    const tourItineraryId = useAppSelector(selectTourItineraryId);
    const currentTour = useAppSelector(selectCurrentTour);

    const [sentTourToVehicleRequest] = useSendTourToVehicleMutation();
    const [sentTourToOptimizerRequest] = useSendTourToOptimizerMutation();

    const handleNavigationEnd = useCallback(() => {
        navigate(DEFAULT_ROUTE, { replace: false });
    }, [navigate]);

    const handleNavigationStart = useCallback(() => {
        dispatch(updatePage(DEFAULT_ROUTE));
        if (!isUndefined(itineraries) && !isUndefined(selectedItinerary)) {
            dispatch(propagateRoutes(itineraries, selectedItinerary));
        }
    }, [dispatch, itineraries, selectedItinerary]);

    const sendToVehicleHandler = async () => {
        if (isUndefined(tourId) || isUndefined(tourItineraryId)) {
            return;
        }

        // Block sync when the SRP-originated tour's departure time has passed the MMT threshold.
        if (currentTour && shouldBlockSyncForExpiredDeparture(currentTour)) {
            setShowPastDepartureDialog(true);
            return;
        }

        setIsLoading(true);

        try {
            const response = await sentTourToVehicleRequest({ id: tourId, itineraryId: tourItineraryId }).unwrap();
            const isVehicleOffline = response?.reason === Reason.VehicleOffline;
            trace.info(`Tour ${tourId}, with itinerary ${tourItineraryId}, was sent to vehicle ${assetId}.`);
            const notificationMessage = intl.formatMessage({
                id: isVehicleOffline
                    ? 'intl-msg:smartRoute.notification.tourQueuedForVehicle'
                    : 'intl-msg:smartRoute.notification.tourSentToVehicle',
            });
            dispatch(updateTourId(undefined));
            showMapNotification(NotificationType.INFO, notificationMessage);
        } catch (error) {
            trace.error(
                `Failed to send tour ${tourId}, with itinerary ${tourItineraryId}, to vehicle ${assetId}.`,
                error
            );
            const tourNotSentToVehicle = intl.formatMessage({
                id: 'intl-msg:smartRoute.notification.tourNotSentToVehicle',
            });
            showMapNotification(NotificationType.WARNING, tourNotSentToVehicle);
        } finally {
            setIsLoading(false);
        }
    };

    const sendToOptimizerHandler = async () => {
        setIsLoading(true);
        if (isUndefined(tourId) || isUndefined(tourItineraryId)) {
            return;
        }

        try {
            await sentTourToOptimizerRequest({ id: tourId, itineraryId: tourItineraryId }).unwrap();
            trace.info(
                `Tour ${tourId}, with itinerary ${tourItineraryId}, was sent to optimizer for vehicle ${assetId}.`
            );
            const tourSentToOptimizer = intl.formatMessage({
                id: 'intl-msg:smartRoute.notification.tourSentToOptimizer',
            });
            dispatch(updateTourId(undefined));
            showMapNotification(NotificationType.INFO, tourSentToOptimizer);
        } catch (error) {
            trace.error(
                `Failed to send tour ${tourId}, with itinerary ${tourItineraryId}, to optimizer for vehicle ${assetId}.`,
                error
            );
            const tourNotSentToOptimizer = intl.formatMessage({
                id: 'intl-msg:smartRoute.notification.tourNotSentToOptimizer',
            });
            showMapNotification(NotificationType.WARNING, tourNotSentToOptimizer);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Fade initial animationStyle={Fade.PAGE_BACK} show={showContent} onExitComplete={handleNavigationEnd}>
            <div className='display-flex flex-column height-100vh' data-testid='route-summary-page'>
                <PastDepartureBlockDialog
                    show={showPastDepartureDialog}
                    onClose={() => setShowPastDepartureDialog(false)}
                />
                <div data-testid='summary-back-button'>
                    <PageNavigationBar title='intl-msg:smartRoute.summary.title' navBack={handleNavigationStart} />
                </div>
                <SmoothScrollbars>
                    <div
                        className='display-flex flex-column padding-x-15 padding-bottom-15'
                        data-testid='route-summary-layout'
                    >
                        {itineraries && (
                            <div className='display-flex flex-column gap-25' data-testid='route-summary-tabs'>
                                <div data-testid='vehicle-settings'>
                                    <div className='text-size-18 text-medium padding-top-15 padding-bottom-5'>
                                        <div className='display-flex align-items-center justify-content-between'>
                                            <FormattedMessage id='intl-msg:smartRoute.generic.vehicle' />
                                        </div>
                                    </div>
                                    <VehicleSelection />
                                </div>

                                <TourSummary itineraries={itineraries} selectedRoute={selectedItinerary ?? 0} />

                                {selectedRouteData && (
                                    <SectionsContainer
                                        itineraries={itineraries}
                                        selectedItinerary={selectedItinerary ?? 0}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </SmoothScrollbars>
                <div
                    className='display-flex flex-1 justify-content-between gap-15 padding-15'
                    data-testid='route-summary-btns'
                >
                    <Flags authorizedFlags={['sendToVehicle']}>
                        <Button
                            className={isLoading ? 'btn-loading' : ''}
                            onClick={sendToVehicleHandler}
                            bsStyle='primary'
                            disabled={isUndefined(assetId) || isUndefined(tourId)}
                        >
                            <span className='rioglyph rioglyph-send' aria-hidden='true' />
                            <FormattedMessage id='intl-msg:smartRoute.sendTourToVehicle.button' />
                        </Button>
                    </Flags>
                    <Flags authorizedFlags={['sendToOptimizer']}>
                        <Button
                            className={isLoading ? 'btn-loading' : ''}
                            onClick={sendToOptimizerHandler}
                            bsStyle='primary'
                            disabled={isUndefined(assetId) || isUndefined(tourId)}
                        >
                            <span className='rioglyph rioglyph-send' aria-hidden='true' />
                            <FormattedMessage id='intl-msg:smartRoute.sendTourToOptimizer.button' />
                        </Button>
                    </Flags>
                    <DownloadPdfButton itineraries={itineraries} />
                </div>
            </div>
        </Fade>
    );
};
